import { City, CivilStatus, Frequency, Gender, SavingOrInvestment } from '@core/constants/data-types.constants';
import { CivilStatusOption } from '@core/models/coinscrap/configurations.model';
import { Config } from '@core/models/config.model';
import { CurrencyAmount } from '@core/models/currency-amount.model';
import { CurrencyExchange } from '@core/models/currency-exchange';
import { IdentityDocument } from '@core/models/identity-document.model';
import {
  FinancialData,
  FuturePlanData,
  InsurancesData,
  LifePlan,
  Person,
  PersonalData,
  ProfessionalData,
  SimplePerson
} from '@core/models/life-plan.model';
import { LiteResponse } from '@core/models/lite/lite.model';
import { Product } from '@core/models/product.model';
import { ProductsPerInsuranceTarget } from '@core/models/products-per-insurance-target.model';
import { LiteResponsesWithLimits } from '@core/services/lite.service';
import { createAction, props } from '@ngrx/store';

export const fullUpdateLifePlan = createAction('[LifePlan] Full Update', props<{ newLifePlan: LifePlan }>());

// AGENT PERSONAL FORM

export const agentTurnedBackLifePlanPersonalData = createAction(
  '[LifePlan] Agent turned back on personal data form',
  props<{ userPersonalData: PersonalData; spousePersonalData?: PersonalData }>()
);

export const agentSavedLifePlanPersonalDataDraft = createAction(
  '[LifePlan] Agent saved draft for personal data',
  props<{ userPersonalData: PersonalData; spousePersonalData?: PersonalData }>()
);

export const agentCompletedLifePlanPersonalData = createAction(
  '[LifePlan] Agent completed personal data form',
  props<{ userPersonalData: PersonalData; spousePersonalData?: PersonalData }>()
);

// AGENT PROFESSIONAL FORM

export const agentTurnedBackLifePlanProfessionalData = createAction(
  '[LifePlan] Agent turned back on professional data form',
  props<{ userProfessionalData: ProfessionalData; spouseProfessionalData?: ProfessionalData }>()
);

export const agentSavedLifePlanProfessionalDataDraft = createAction(
  '[LifePlan] Agent saved draft for professional data',
  props<{ userProfessionalData: ProfessionalData; spouseProfessionalData?: ProfessionalData }>()
);

export const agentCompletedLifePlanProfessionalData = createAction(
  '[LifePlan] Agent completed professional data form',
  props<{ userProfessionalData: ProfessionalData; spouseProfessionalData?: ProfessionalData }>()
);

// AGENT FINANCIAL FORM

export const agentTurnedBackLifePlanFinancialData = createAction(
  '[LifePlan] Agent turned back on financial data form',
  props<{ userFinancialData: FinancialData; spouseFinancialData?: FinancialData }>()
);

export const agentSavedLifePlanFinacialDataDraft = createAction(
  '[LifePlan] Agent saved draft for financial data',
  props<{ userFinancialData: FinancialData; spouseFinancialData?: FinancialData }>()
);

export const agentCompletedLifePlanFinancialData = createAction(
  '[LifePlan] Agent completed financial data form',
  props<{ userFinancialData: FinancialData; spouseFinancialData?: FinancialData }>()
);

// AGENT INSURANCES FORM

export const agentTurnedBackLifePlanInsurancesData = createAction(
  '[LifePlan] Agent turned back on insurances data form',
  props<{ userInsurancesData: InsurancesData; spouseInsurancesData?: InsurancesData }>()
);

export const agentSavedLifePlanInsurancesDataDraft = createAction(
  '[LifePlan] Agent saved draft for insurances data',
  props<{ userInsurancesData: InsurancesData; spouseInsurancesData?: InsurancesData }>()
);

export const agentCompletedLifePlanInsurancesData = createAction(
  '[LifePlan] Agent completed insurances data form',
  props<{ userInsurancesData: InsurancesData; spouseInsurancesData?: InsurancesData }>()
);

// EDIT FORMS

export const completedLifePlanPersonalDataEdit = createAction(
  '[LifePlan] Completed personal data edit form',
  props<{ userPersonalData: PersonalData; spousePersonalData?: PersonalData }>()
);

export const completedLifePlanProfessionalDataEdit = createAction(
  '[LifePlan] Completed professional data edit form',
  props<{ userProfessionalData: ProfessionalData; spouseProfessionalData?: ProfessionalData }>()
);

export const completedLifePlanFinancialDataEdit = createAction(
  '[LifePlan] Completed financial data edit form',
  props<{ userFinancialData: FinancialData; spouseFinancialData?: FinancialData }>()
);

export const completedFrequencyEdit = createAction(
  '[LifePlan] Completed frequency form',
  props<{ frequency: Frequency }>()
);

export const completedLifePlanInsurancesDataEdit = createAction(
  '[LifePlan] Completed insurances data edit form',
  props<{ userInsurancesData: InsurancesData; spouseInsurancesData?: InsurancesData }>()
);

// FUTURE PLANS

export const completedFuturePlansEdit = createAction(
  '[LifePlan] Completed future plans edit',
  props<{ futurePlans: FuturePlanData[] }>()
);

// COMPLETE FORM

export const completedCompleteFormEdit = createAction(
  '[LifePlan] Completed complete form edit',
  props<{ user: Person; spouse?: Person }>()
);

// INSURANCE TARGETS

export const generateLifePlanInsuranceTargets = createAction(
  '[LifePlan] Generate life plan insurance targets',
  props<{ config: Config }>()
);

export const updateLifePlanInsuranceTargetsWithRecommendations = createAction(
  '[LifePlan] Update life plan insurance targets with recommendations',
  props<{ productsPerInsuranceTarget: ProductsPerInsuranceTarget }>()
);

export const updateLifePlanCurrentInsuranceTarget = createAction(
  '[LifePlan] Update life plan current insurance target',
  props<{ insuranceTargetId: string }>()
);

export const updateLifePlanSavingInsuranceTargetTotalAmount = createAction(
  '[LifePlan] Update life plan saving insurance target total amount',
  props<{ insuranceTargetId: string; totalAmount: CurrencyAmount }>()
);

export const updateLifePlanSavingInsuranceTargetSavings = createAction(
  '[LifePlan] Update life plan saving insurance target savings',
  props<{ insuranceTargetId: string; savings: CurrencyAmount }>()
);

export const updateLifePlanInsuranceTargetCurrentProduct = createAction(
  '[LifePlan] Update life plan insurance target current product',
  props<{ insuranceTargetId: string; product: Product }>()
);

export const updateLifePlanInsuranceTargetCurrentProductTemporality = createAction(
  '[LifePlan] Update life plan insurance target current product temporality',
  props<{ insuranceTargetId: string; temporalityId: string }>()
);

export const updateLifePlanInsuranceTargetCurrentProductWithLiteValues = createAction(
  '[LifePlan] Update life plan insurance target current product with lite values',
  props<{
    insuranceTargetId: string;
    productId: string;
    liteResponse: LiteResponse;
  }>()
);

export const updateLifePlanInsuranceTargetCurrentProductWithLiteValuesAndLimits = createAction(
  '[LifePlan] Update life plan insurance target current product with lite values and limits',
  props<{
    insuranceTargetId: string;
    productId: string;
    liteResponses: LiteResponsesWithLimits;
  }>()
);

export const updateLifePlanInsuranceTargetCurrentProductCurrencies = createAction(
  '[LifePlan] Update life plan insurance target current product currencies',
  props<{
    insuranceTargetId: string;
    productId: string;
    currencyExchange: CurrencyExchange;
  }>()
);

export const updateAllLifePlanInsuranceTargetsCurrentProducts = createAction(
  '[LifePlan] Update all life plan insurance targets current products'
);

export const updateLifePlanCurrentProductOnCart = createAction(
  '[LifePlan] Update life plan current product on cart',
  props<{ insuranceTargetId: string; productId: string; onCart: boolean }>()
);

export const updateLifePlanSeveralProductsOnCart = createAction(
  '[LifePlan] Update life plan several products on cart',
  props<{ insuranceTargets: string[]; onCart: boolean }>()
);

export const updateDefaultLifePlanCurrentProductOnCart = createAction(
  '[LifePlan] Update default life plan current product on cart',
  props<{ insuranceTargetId: string; productId: string; onCart: boolean }>()
);

export const updateInsuranceTargetUniversity = createAction(
  '[LifePlan] Update insurance target university',
  props<{ insuranceTargetId: string; university: string; universityCost: CurrencyAmount }>()
);

// Life Plan management

export const forceUpdateDefaultLifePlan = createAction(
  '[LifePlan] Force update default life plan',
  props<{ defaultLifePlan: LifePlan }>()
);

export const updateSelectedLifePlan = createAction(
  '[LifePlan] Update selected life plan',
  props<{ selectedLifePlan: 'current' | 'default' }>()
);

export const cleanLifePlanState = createAction('[LifePlan] Clean life plan state');

export const updateInBackgroundLifePlan = createAction('[LifePlan] Update in background');
export const updatedInBackgroundLifePlanSuccess = createAction('[LifePlan] Updated in background life plan success');
export const updatedInBackgroundLifePlanError = createAction('[LifePlan] Updated in background life plan error');
